import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { displayError, displaySuccess } from '@/utils/displayMessageUtils';
import {
  Card,
  Divider,
  Skeleton,
  Typography,
  Flex,
  Input,
  Button,
  Row,
  Col,
  message,
} from 'antd';
import {
  useChangePasswordWithOldPasswordMutation,
  useDisable2FAMutation,
  useEnable2FAMutation,
  useVerifyEnable2FAMutation,
} from '../../api/profileAPI';
import Paragraph from 'antd/es/typography/Paragraph';
import { InputField } from '@/components/Form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

const passwordRules = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{5,}$/;

const settingsSchema = yup.object().shape({
  currentPassword: yup.string().required('Current password is required'),
  newPassword: yup
    .string()
    .min(8, 'Password must be at least 8 characters')
    .matches(/[A-Z]/, 'Password must contain at least one capital letter')
    .matches(
      /[^a-zA-Z0-9]/,
      'Password must contain at least one special character',
    )
    .required('Password is required'),
  confirmPassword: yup
    .string()
    .required('Password is required')
    .oneOf([yup.ref('newPassword')], 'Passwords must match'),
});

const { Title } = Typography;
const Settings = () => {
  const navigate = useNavigate();
  const [secret, setSecret] = useState('');
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);

  const [postEnable2FA, { isLoading: enable2FALoading }] =
    useEnable2FAMutation();
  const [verify2FACode, { isLoading: verify2FALoading }] =
    useVerifyEnable2FAMutation();
  const [disable2FACode, { isLoading: disable2FALoading }] =
    useDisable2FAMutation();

  const [changePassword, { isLoading: changePasswordLoading }] =
    useChangePasswordWithOldPasswordMutation();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(settingsSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const onSubmit = async (data: any) => {
    const { currentPassword, newPassword } = data;
    try {
      const response = await changePassword({
        old_password: currentPassword,
        new_password: newPassword,
      }).unwrap();
      displaySuccess(response.message);
    } catch (error: any) {
      displayError(error);
    }
  };

  useEffect(() => {
    postEnable2FA()
      .unwrap()
      .then((res) => {
        if (res.data.two_factor_enabled) {
          setIs2FAEnabled(true);
        } else {
          setSecret(res.data.secret);
          setQrCode(res.data.qr_code);
        }
      })
      .catch((err) => {
        displayError(err);
        // navigate('/')
      });
  }, []);

  const verifyCode = () => {
    verify2FACode({ code: Number(code) })
      .unwrap()
      .then((res) => {
        displaySuccess('2FA enabled successfully!');
      })
      .catch((err) => {
        displayError(err);
      });
  };

  const disable2FA = () => {
    disable2FACode({ code: Number(code) })
      .unwrap()
      .then((res) => {
        displaySuccess(res.data.message);
        // setIs2FAEnabled(false)
      })
      .catch((err) => {
        displayError(err);
      });
  };

  return (
    <div
      style={{
        width: '100%',
      }}
    >
      <div>
        {/* <Typography.Title level={3}>
          {is2FAEnabled
            ? 'Disable 2 Factor Authentication'
            : 'Setup 2 Factor Authentication'}
        </Typography.Title>

        <Card>
          {!is2FAEnabled && (
            <Row gutter={[16, 16]} justify="center">
              <Col xs={24} sm={24} lg={24} xl={10}>
                <Card>
                  {qrCode ? (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <img
                        src={`data:image/png;base64,${qrCode}`}
                        alt="2FA QR Code"
                        style={{
                          width: '100%',
                          height: 'auto',
                          maxWidth: '400px',
                          marginBottom: '1rem',
                        }}
                      />
                    </div>
                  ) : (
                    <Skeleton.Image />
                  )}
                </Card>
              </Col>
              <Col
                xs={24}
                sm={24}
                lg={24}
                xl={14}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  padding: '1rem',
                }}
              >
                <ul className="number-list" style={{ padding: '0' }}>
                  <li>
                    <Title
                      level={5}
                      style={{ marginBottom: '1rem', marginTop: '0' }}
                    >
                      You will need an authenticator mobile app to complete this
                      process, such as one of the following:
                    </Title>
                    <Paragraph style={{ margin: '0' }} type="success">
                      Google Authenticator
                    </Paragraph>
                    <Paragraph style={{ margin: '0' }} type="success">
                      Microsoft Authenticator
                    </Paragraph>
                  </li>
                  <li>
                    <Title level={5} style={{ margin: '0 0 1rem' }}>
                      Scan the QR code with your authenticator
                    </Title>
                    <Paragraph type="secondary">
                      If you can't scan the code, you can enter this secret key
                      into your authenticator app. The secret key is:
                    </Paragraph>
                    <Input value={secret} readOnly={true} />
                  </li>
                  <li>
                    <Title level={5} style={{ margin: '0 0 1rem' }}>
                      After scanning the QR code above, enter the six-digit code
                      generated by your authenticator
                    </Title>
                    <Input.OTP
                      id="code"
                      value={code}
                      onChange={(e) => setCode(e)}
                      length={6}
                      size="large"
                    />
                  </li>
                </ul>

                <Button
                  onClick={verifyCode}
                  disabled={code.length < 6 || verify2FALoading}
                  type="primary"
                  size="large"
                  loading={verify2FALoading}
                >
                  Verify now
                </Button>
              </Col>
            </Row>
          )}
          {is2FAEnabled && (
            <div>
              <div style={{ margin: '0 0 1rem' }}>
                <Title level={5} style={{ margin: '0 0 1rem' }}>
                  To disable 2FA verification, please enter the 2FA code and
                  submit
                </Title>
                <Input.OTP
                  id="code"
                  value={code}
                  onChange={(e) => setCode(e)}
                  length={6}
                  size="large"
                />
              </div>
              <Button
                onClick={disable2FA}
                disabled={code.length < 6 || disable2FALoading}
                type="primary"
                size="large"
                loading={disable2FALoading}
              >
                Disable now
              </Button>
            </div>
          )}
        </Card> */}

        <Typography.Title level={3} style={{ margin: '0 0 12px' }}>
          Change Password
        </Typography.Title>
        <Divider style={{ marginTop: 0, marginBottom: 20 }} />

        <Card>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Row gutter={[16, 16]}>
              <Col xs={24}>
                <div>
                  <InputField
                    control={control}
                    label="Current Password"
                    type="password"
                    size="large"
                    name="currentPassword"
                    error={errors?.currentPassword?.message ?? ''}
                  />
                  {/* <span
                  style={{
                    float: 'right',
                    textDecoration: 'underline',
                    color: 'red',
                    cursor: 'pointer',
                  }}
                >
                  Forgot your password?{' '}
                </span> */}
                </div>
              </Col>
              <Col xs={24} xl={12}>
                <InputField
                  control={control}
                  label="New Password"
                  type="password"
                  size="large"
                  name="newPassword"
                  error={errors?.newPassword?.message ?? ''}
                />
              </Col>
              <Col xs={24} xl={12}>
                <InputField
                  control={control}
                  label="Confirm New Password"
                  type="password"
                  size="large"
                  name="confirmPassword"
                  error={errors?.confirmPassword?.message ?? ''}
                />
              </Col>
              <div
                style={{
                  display: 'flex',
                  width: '100%',
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  htmlType="submit"
                  type="primary"
                  size="large"
                  loading={changePasswordLoading}
                >
                  Update Password
                </Button>
              </div>
            </Row>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default Settings;
